import { useMemo, useCallback, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TrendingUp, TrendingDown, Minus, Calculator, PieChart } from "lucide-react";
import { calculateOurOfferMetrics } from "../utils/calculations";

export const SensitivityAnalysis = ({ formData, setFormData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, plData }) => {
  // State for custom profit calculator - initialize with formData values
  const [customConstructionCost, setCustomConstructionCost] = useState(formData.constructionCostPerSqft || "2800");
  const [customFreeArea, setCustomFreeArea] = useState(formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent || "0");
  const [customSaleRate, setCustomSaleRate] = useState(formData.saleRatePerSqft || formData.saleRateFlats || "15000");
  // Toggle between Free Area % and Area Sharing Ratio
  const [areaInputMode, setAreaInputMode] = useState("freeArea"); // "freeArea" or "sharingRatio"
  const [customSocietyShare, setCustomSocietyShare] = useState("30"); // Society's % of total carpet
  // RR Cost Multiplier (default 5%)
  const [rrMultiplier, setRRMultiplier] = useState(formData.rrMultiplier || "5");

  // Sync custom values when formData changes
  useEffect(() => {
    if (formData.constructionCostPerSqft) {
      setCustomConstructionCost(formData.constructionCostPerSqft);
    }
    if (formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent) {
      setCustomFreeArea(formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent);
    }
    if (formData.saleRatePerSqft || formData.saleRateFlats) {
      setCustomSaleRate(formData.saleRatePerSqft || formData.saleRateFlats);
    }
    if (formData.rrMultiplier) {
      setRRMultiplier(formData.rrMultiplier);
    }
  }, [formData.constructionCostPerSqft, formData.freeAreaPercentFlats, formData.additionalFreeAreaPercent, formData.saleRatePerSqft, formData.saleRateFlats, formData.rrMultiplier]);

  // Use shared calculation for base profit (EXACT match with P&L Sheet)
  const baseMetrics = useMemo(() => {
    return calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts]);

  // Update formData when RR Multiplier changes
  const handleRRMultiplierChange = (value) => {
    setRRMultiplier(value);
    if (setFormData) {
      setFormData({...formData, rrMultiplier: value});
    }
  };

  // Calculate area sharing based on free area percentage
  const calculateAreaSharing = useCallback((freeAreaPercent) => {
    if (!fsiBreakdown) return { societyShare: 0, builderShare: 0 };
    
    const conversionFactor = 0.092903;
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    
    // Existing carpet area
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // Free area from percentage
    const additionalFreeCarpet = existingCarpetArea * (freeAreaPercent / 100);
    
    // Terrace/Garden converted area (separate ratios - matching calculations.js)
    const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
    const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
    const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
    const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
    const convertedTerrace = areaTerrace * (terraceConversionRatio / 100);
    const convertedGarden = areaGarden * (gardenConversionRatio / 100);
    const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
    const baseFreePercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    const incrementOnTG = applyIncrementOnTG ? (convertedTerrace + convertedGarden) * (baseFreePercent / 100) : 0;
    const convertedTerraceGarden = convertedTerrace + convertedGarden + incrementOnTG;

    // Parking compensation
    const parkingCompensationCarpet = formData.parkingCompensationType === "carpet"
      ? (parseFloat(formData.compensationCarpetArea) || 0) * conversionFactor
      : 0;
    
    // Total free area = additional % free + converted terrace/garden + parking
    const totalFreeArea = additionalFreeCarpet + convertedTerraceGarden + parkingCompensationCarpet;
    
    // Society area = existing + total free
    const societyArea = existingCarpetArea + totalFreeArea;
    const builderArea = Math.max(0, totalCarpetArea - societyArea);
    
    // Calculate percentages
    const societyShare = totalCarpetArea > 0 ? (societyArea / totalCarpetArea) * 100 : 0;
    const builderShare = totalCarpetArea > 0 ? (builderArea / totalCarpetArea) * 100 : 0;
    
    return { 
      societyShare: Math.round(societyShare * 10) / 10, 
      builderShare: Math.round(builderShare * 10) / 10,
      societyArea,
      builderArea,
      totalCarpetArea
    };
  }, [fsiBreakdown, formData.carpetAreaFactor, formData.areaFlats, formData.areaOffices, formData.areaShops,
      formData.areaTerrace, formData.areaGarden, formData.terraceConversionRatio, formData.gardenConversionRatio,
      formData.applyIncrementOnTerraceGarden, formData.freeAreaPercentFlats, formData.additionalFreeAreaPercent,
      formData.parkingCompensationType, formData.compensationCarpetArea]);

  // Convert society share % back to free area %
  const societyShareToFreeArea = useCallback((societySharePercent) => {
    if (!fsiBreakdown) return 0;
    
    const conversionFactor = 0.092903;
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // societyArea = existingCarpetArea + existingCarpetArea * (freeArea / 100)
    // societyArea = totalCarpetArea * (societySharePercent / 100)
    // So: totalCarpetArea * (societySharePercent / 100) = existingCarpetArea * (1 + freeArea / 100)
    // freeArea = ((totalCarpetArea * societySharePercent / 100) / existingCarpetArea - 1) * 100
    
    if (existingCarpetArea === 0) return 0;
    const societyArea = totalCarpetArea * (societySharePercent / 100);
    const freeAreaPercent = ((societyArea / existingCarpetArea) - 1) * 100;
    return Math.max(0, Math.round(freeAreaPercent * 10) / 10);
  }, [fsiBreakdown, formData.carpetAreaFactor, formData.areaFlats, formData.areaOffices, formData.areaShops]);

  // Calculate profit for given overrides on sale rate, free area, or construction cost
  // Delegates to the canonical calculateOurOfferMetrics to ensure consistency with P&L
  const calculateProfit = useCallback((overrides = {}) => {
    if (!fsiBreakdown) return 0;

    const modifiedFormData = { ...formData };
    if (overrides.saleRate !== undefined) {
      // Apply sale rate change to ALL property types proportionally
      const baseFlatRate = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
      const delta = overrides.saleRate - baseFlatRate;
      modifiedFormData.saleRateFlats = String(overrides.saleRate);
      // Apply same delta to offices/shops if they have values
      if (formData.saleRateOffices) {
        modifiedFormData.saleRateOffices = String(parseFloat(formData.saleRateOffices) + delta);
      }
      if (formData.saleRateShops) {
        modifiedFormData.saleRateShops = String(parseFloat(formData.saleRateShops) + delta);
      }
    }
    if (overrides.freeAreaPercent !== undefined) {
      modifiedFormData.freeAreaPercentFlats = String(overrides.freeAreaPercent);
      modifiedFormData.additionalFreeAreaPercent = String(overrides.freeAreaPercent);
    }
    if (overrides.constructionCost !== undefined) {
      modifiedFormData.constructionCostPerSqft = String(overrides.constructionCost);
    }
    // Inject an FSI cost override (used by Basic-FSI sensitivity)
    const fsiCostsForCalc = overrides.fsiCostOverride !== undefined
      ? { ...fsiCosts, totalCosts: overrides.fsiCostOverride }
      : fsiCosts;
    const result = calculateOurOfferMetrics(modifiedFormData, fsiBreakdown, fsiCostsForCalc, daChargesCosts, societyCosts);
    // For basic-FSI sensitivity we need both profit and initial investment
    if (overrides.returnFullMetrics) return result;
    return result?.grossProfit ?? 0;
  }, [fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, formData]);

  // Base values from shared calculations (matches P&L Sheet exactly)
  const baseSaleRate = parseFloat(formData.saleRatePerSqft) || parseFloat(formData.saleRateFlats) || 0;
  const baseFreeAreaPercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const baseProfit = baseMetrics?.grossProfit || 0;

  // Sale Rate Sensitivity Data (5 scenarios: -1000, -500, base, +500, +1000)
  const saleRateSensitivity = useMemo(() => {
    const scenarios = [-1000, -500, 0, 500, 1000];
    return scenarios.map(change => ({
      change,
      saleRate: baseSaleRate + change,
      profit: change === 0 ? baseProfit : calculateProfit({ saleRate: baseSaleRate + change }),
      isBase: change === 0
    }));
  }, [baseSaleRate, baseProfit, calculateProfit]);

  // Free Area Sensitivity Data (5 scenarios: +1% to +5%)
  const freeAreaSensitivity = useMemo(() => {
    return [1, 2, 3, 4, 5].map(additionalPercent => ({
      additionalPercent,
      totalFreePercent: baseFreeAreaPercent + additionalPercent,
      profit: calculateProfit({ freeAreaPercent: baseFreeAreaPercent + additionalPercent })
    }));
  }, [baseFreeAreaPercent, calculateProfit]);

  // Construction Cost Sensitivity Data (5 scenarios: -200, -100, base, +100, +200)
  const baseConstructionCost = parseFloat(formData.constructionCostPerSqft) || 0;
  const constructionCostSensitivity = useMemo(() => {
    const scenarios = [-200, -100, 0, 100, 200];
    return scenarios.map(change => ({
      change,
      constructionCost: baseConstructionCost + change,
      profit: change === 0 ? baseProfit : calculateProfit({ constructionCost: baseConstructionCost + change }),
      isBase: change === 0
    }));
  }, [baseConstructionCost, baseProfit, calculateProfit]);

  // Basic-FSI (1.1 only) Sensitivity — compares Normal vs Basic-only vs 3 intermediate blends
  // Uses fsiCosts.totalCostsBasicMode when available.
  const basicFSISensitivity = useMemo(() => {
    const fullCost = fsiCosts?.totalCosts || 0;
    const basicCost = fsiCosts?.totalCostsBasicMode;
    if (basicCost == null || fullCost <= basicCost) return null; // no meaningful delta
    // 5 scenarios: 100% Normal, 75% blend, 50% blend, 25% blend, 100% Basic
    const scenarios = [
      { label: "Full FSI", blendPct: 100 },
      { label: "75%", blendPct: 75 },
      { label: "50%", blendPct: 50 },
      { label: "25%", blendPct: 25 },
      { label: "Basic 1.1 only", blendPct: 0 },
    ];
    return scenarios.map(sc => {
      const fsiCostForScenario = basicCost + ((fullCost - basicCost) * (sc.blendPct / 100));
      const metrics = sc.blendPct === 100
        ? plData
        : calculateProfit({ fsiCostOverride: fsiCostForScenario, returnFullMetrics: true });
      return {
        label: sc.label,
        blendPct: sc.blendPct,
        fsiCost: fsiCostForScenario,
        savingsVsFull: fullCost - fsiCostForScenario,
        profit: metrics?.grossProfit ?? 0,
        initialInvestment: metrics?.initialInvestment ?? 0,
        isBase: sc.blendPct === 100,
      };
    });
  }, [fsiCosts, baseProfit, calculateProfit, plData]);

  // Calculate base area sharing
  const baseAreaSharing = useMemo(() => {
    return calculateAreaSharing(baseFreeAreaPercent);
  }, [baseFreeAreaPercent, calculateAreaSharing]);

  // Custom profit calculated using DELTA approach from base profit
  // Only the specific changed heads affect the profit
  const customProfit = useMemo(() => {
    if (!fsiBreakdown) return baseProfit;
    
    const conversionFactor = 0.092903;
    
    // Get base values from formData (what's entered in the main tabs)
    const baseCC = parseFloat(formData.constructionCostPerSqft) || 0;
    const baseSR = parseFloat(formData.saleRatePerSqft) || parseFloat(formData.saleRateFlats) || 0;
    const baseFAP = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    const baseRRMultiplier = parseFloat(formData.rrMultiplier) || 5;
    
    // Get custom values from calculator inputs
    const customCC = parseFloat(customConstructionCost) || baseCC;
    const customSR = parseFloat(customSaleRate) || baseSR;
    const customRR = parseFloat(rrMultiplier) || baseRRMultiplier;
    
    // Determine effective free area percentage
    let effectiveFreeAreaPercent;
    if (areaInputMode === "sharingRatio") {
      effectiveFreeAreaPercent = societyShareToFreeArea(parseFloat(customSocietyShare) || 0);
    } else {
      const customFreeValue = parseFloat(customFreeArea);
      effectiveFreeAreaPercent = (customFreeValue === undefined || isNaN(customFreeValue)) 
        ? baseFAP 
        : customFreeValue;
    }
    
    // Calculate areas needed for delta calculations
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    const loadingPct = parseFloat(formData.loadingPercentage) || 40;
    
    // Total constructed area (for construction cost impact)
    const totalConstructedArea = totalCarpetArea + (totalCarpetArea * loadingPct / 100);
    const totalConstructedAreaSqft = totalConstructedArea / conversionFactor;
    
    // Existing carpet area
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // Terrace/Garden converted area (SEPARATE ratios)
    const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
    const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
    const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
    const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
    const convertedTerrace = areaTerrace * (terraceConversionRatio / 100);
    const convertedGarden = areaGarden * (gardenConversionRatio / 100);
    const convertedTerraceGarden = convertedTerrace + convertedGarden;
    
    // BASE saleable area (with base free area %)
    const baseAdditionalFree = existingCarpetArea * (baseFAP / 100);
    const baseTotalFree = baseAdditionalFree + convertedTerraceGarden;
    const baseBalanceCarpet = Math.max(0, totalCarpetArea - existingCarpetArea - baseTotalFree);
    const baseSaleableAreaSqft = (baseBalanceCarpet * (1 + loadingPct / 100)) / conversionFactor;
    
    // CUSTOM saleable area (with custom free area %)
    const customAdditionalFree = existingCarpetArea * (effectiveFreeAreaPercent / 100);
    const customTotalFree = customAdditionalFree + convertedTerraceGarden;
    const customBalanceCarpet = Math.max(0, totalCarpetArea - existingCarpetArea - customTotalFree);
    const customSaleableAreaSqft = (customBalanceCarpet * (1 + loadingPct / 100)) / conversionFactor;
    
    // ============ CALCULATE DELTAS ============
    
    // 1. Construction Cost Impact
    // Delta = (baseCC - customCC) × constructedArea
    // Reducing cost = positive impact (saves money)
    const ccDelta = baseCC - customCC;
    const constructionImpact = ccDelta * totalConstructedAreaSqft;
    
    // 2. Sale Rate Impact  
    // Delta = (customSR - baseSR) × baseSaleableArea
    // Increasing rate = positive impact (more revenue)
    const srDelta = customSR - baseSR;
    const saleRateImpact = srDelta * baseSaleableAreaSqft;
    
    // 3. Free Area Impact
    // If free area changes, saleable area changes
    // Impact = (customSaleableArea - baseSaleableArea) × baseSaleRate
    // Less free area = more saleable area = positive impact
    const saleableAreaDelta = customSaleableAreaSqft - baseSaleableAreaSqft;
    const freeAreaImpact = saleableAreaDelta * baseSR;
    
    // 4. RR Multiplier Impact
    // RR-based costs: TDR costs (Amenity + Slum), Paid FSI, Ancillary FSI
    // These are calculated as: Area × RR Rate × (multiplier/100)
    // When RR multiplier increases, these costs increase = negative impact
    const rrBasedCosts = (fsiCosts?.amenityTDRCost || 0) + (fsiCosts?.slumTDRCost || 0) + 
                         (fsiCosts?.paidFSICost || 0) + (fsiCosts?.ancillaryFSICost || 0);
    // Calculate the proportional change in RR-based costs
    // If base multiplier is 5% and custom is 6%, costs increase by (6-5)/5 = 20%
    const rrDelta = customRR - baseRRMultiplier;
    const rrImpact = baseRRMultiplier > 0 
      ? -(rrBasedCosts * (rrDelta / baseRRMultiplier))
      : 0;
    
    // Total adjusted profit = base profit + all impacts
    const totalImpact = constructionImpact + saleRateImpact + freeAreaImpact + rrImpact;
    
    return baseProfit + totalImpact;
  }, [fsiBreakdown, fsiCosts, customConstructionCost, customFreeArea, customSaleRate,
      customSocietyShare, areaInputMode, rrMultiplier, societyShareToFreeArea, baseProfit,
      formData.constructionCostPerSqft, formData.saleRatePerSqft, formData.saleRateFlats,
      formData.freeAreaPercentFlats, formData.additionalFreeAreaPercent, formData.rrMultiplier,
      formData.carpetAreaFactor, formData.loadingPercentage,
      formData.areaFlats, formData.areaOffices, formData.areaShops,
      formData.areaTerrace, formData.areaGarden,
      formData.terraceConversionRatio, formData.gardenConversionRatio]);

  // Custom area sharing for display
  // If custom free area is 0 or not changed, use base area sharing
  const customAreaSharing = useMemo(() => {
    const baseFAP = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    
    let effectiveFreeArea;
    if (areaInputMode === "sharingRatio") {
      effectiveFreeArea = societyShareToFreeArea(parseFloat(customSocietyShare) || 0);
    } else {
      const customFreeValue = parseFloat(customFreeArea);
      effectiveFreeArea = (customFreeValue === 0 || isNaN(customFreeValue)) 
        ? baseFAP 
        : customFreeValue;
    }
    return calculateAreaSharing(effectiveFreeArea);
  }, [customFreeArea, customSocietyShare, areaInputMode, calculateAreaSharing, societyShareToFreeArea, formData.freeAreaPercentFlats, formData.additionalFreeAreaPercent]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCrores = (value) => {
    const crores = value / 10000000;
    return `₹${crores.toFixed(2)} Cr`;
  };

  // Calculate Investment Summary (upfront costs at start of project)
  // Uses same formula as calculations.js (single source of truth)
  const investmentSummary = useMemo(() => {
    if (!fsiCosts || !daChargesCosts || !societyCosts) return null;

    // TDR Cost (Amenity + Slum TDR)
    const tdrCost = (fsiCosts.amenityTDRCost || 0) + (fsiCosts.slumTDRCost || 0);
    
    // Paid FSI + Ancillary costs (full amount)
    const paidFSIAncillaryFull = (fsiCosts.paidFSICost || 0) + (fsiCosts.ancillaryFSICost || 0);
    
    // Apply 20% factor to Paid FSI + Ancillary with minimum ₹50 Lakhs
    const twentyPercent = paidFSIAncillaryFull * 0.20;
    const minAmount = 5000000; // ₹50 Lakhs
    const paidFSIAncillaryInvestment = Math.max(twentyPercent, minAmount);
    
    // DA Charges
    const daCharges = daChargesCosts.totalDACharges || 0;
    
    // Society upfront costs
    const shiftingCharges = (societyCosts.shiftingCharges || 0) * 0.5; // 50% shifting at start
    const bettermentPaymentTiming = formData.bettermentPaymentTiming || "at_possession";
    const bettermentCharges = bettermentPaymentTiming === "split" ? (societyCosts.bettermentCharges || 0) * 0.5 : 0;
    
    // ULC Charges (if applicable)
    const ulcCharges = fsiCosts.ulcCharges || 0;
    
    // Other FSI/Sanction costs (excluding TDR, Paid FSI, Ancillary, ULC)
    const sanctionCosts = (fsiCosts.totalCosts || 0) - tdrCost - paidFSIAncillaryFull - ulcCharges;
    
    // Bank Guarantee (full amount as deposit)
    const bgBaseAmount = (formData.bankGuaranteeRequired === "yes" || formData.bankGuaranteeRequired === true) 
      ? (parseFloat(formData.bankGuaranteeAmount) || 0) : 0;
    const depositCost = societyCosts.depositCost || 0;
    const brokerageCostSociety = societyCosts.brokerageCost || 0;
    
    // PMC is NOT part of initial investment
    const totalInvestment = tdrCost + paidFSIAncillaryInvestment + sanctionCosts + daCharges + 
                            shiftingCharges + bettermentCharges + ulcCharges + bgBaseAmount +
                            depositCost + brokerageCostSociety;

    // ============ BASIC FSI (1.1 + full Ancillary) SCENARIO ============
    // Alternate initial investment when developer defers TDR + Paid FSI purchase and sanctions only Basic FSI.
    // fsiCosts.totalCostsBasicMode is set by the FSISanctionCosts component and already excludes
    // (or proportionally scales down) TDR / Paid FSI while keeping full Ancillary + area-independent costs.
    let basicMode = null;
    const totalCostsBasicMode = fsiCosts.totalCostsBasicMode;
    if (totalCostsBasicMode != null && totalCostsBasicMode > 0) {
      // Basic sanction cost excludes ULC (billed separately below)
      const basicSanctionCosts = Math.max(0, totalCostsBasicMode - ulcCharges);
      const basicTotalInvestment = basicSanctionCosts + daCharges + shiftingCharges +
                                    bettermentCharges + ulcCharges + bgBaseAmount +
                                    depositCost + brokerageCostSociety;
      basicMode = {
        basicSanctionCosts,
        daCharges,
        shiftingCharges,
        bettermentCharges,
        ulcCharges,
        bgBaseAmount,
        depositCost,
        brokerageCostSociety,
        basicTotalInvestment,
        savingsVsFull: Math.max(0, totalInvestment - basicTotalInvestment),
      };
    }

    return {
      tdrCost,
      paidFSIAncillaryFull,
      paidFSIAncillaryInvestment,
      twentyPercent,
      minAmount,
      sanctionCosts,
      daCharges,
      shiftingCharges,
      bettermentCharges,
      ulcCharges,
      bgBaseAmount,
      depositCost,
      brokerageCostSociety,
      totalInvestment,
      basicMode,
    };
  }, [fsiCosts, daChargesCosts, societyCosts, formData.bettermentPaymentTiming, formData.bankGuaranteeRequired, formData.bankGuaranteeAmount]);

  // Calculate detailed costs for custom profit calculator display
  const costBreakdown = useMemo(() => {
    if (!fsiCosts || !daChargesCosts || !societyCosts) return null;

    const conversionFactor = 0.092903;
    const totalFSIArea = fsiBreakdown?.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    const loadingPct = parseFloat(formData.loadingPercentage) || 40;
    const totalConstructedArea = totalCarpetArea + (totalCarpetArea * loadingPct / 100);
    const totalConstructedAreaSqft = totalConstructedArea / conversionFactor;
    
    // Existing carpet area
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // Free area with custom value
    let effectiveFreeArea;
    if (areaInputMode === "sharingRatio") {
      effectiveFreeArea = societyShareToFreeArea(parseFloat(customSocietyShare) || 0);
    } else {
      effectiveFreeArea = parseFloat(customFreeArea) || 0;
    }
    const additionalFreeCarpet = existingCarpetArea * (effectiveFreeArea / 100);
    const balanceCarpetArea = Math.max(0, totalCarpetArea - existingCarpetArea - additionalFreeCarpet);
    const finalSaleArea = balanceCarpetArea * (1 + loadingPct / 100);
    const finalSaleAreaSqft = finalSaleArea / conversionFactor;

    // Construction cost
    const constructionCostPerSqft = parseFloat(customConstructionCost) || 0;
    const totalConstructionCost = totalConstructedAreaSqft * constructionCostPerSqft;

    // Other costs
    const marketingCostPerSqft = parseFloat(formData.marketingCostPerSqft) || 0;
    const totalMarketingCost = finalSaleAreaSqft * marketingCostPerSqft;
    const miscExpPerSqft = parseFloat(formData.miscExpPerSqft) || 0;
    const totalMiscExp = finalSaleAreaSqft * miscExpPerSqft;
    
    let totalBrokerageCommission = 0;
    if (formData.brokerageCommissionType === "fixed") {
      totalBrokerageCommission = parseFloat(formData.brokerageCommissionFixed) || 0;
    } else {
      const brokeragePerSqft = parseFloat(formData.brokerageCommissionPerSqft) || 0;
      totalBrokerageCommission = finalSaleAreaSqft * brokeragePerSqft;
    }

    // Bank Interest
    const duration = parseInt(formData.projectDuration) || 0;
    const durationYears = duration / 12;
    const projectInterestRate = formData.projectInterestRate !== undefined && formData.projectInterestRate !== "" 
      ? parseFloat(formData.projectInterestRate) 
      : 12;
    const fsiTotalCosts = fsiCosts?.totalCosts || 0;
    const baseCostsForInterest = fsiTotalCosts + (totalConstructionCost * 0.15);
    const totalBankInterest = baseCostsForInterest * (projectInterestRate / 100) * durationYears;

    // Revenue
    const saleRate = parseFloat(customSaleRate) || 0;
    const totalRevenue = finalSaleAreaSqft * saleRate;

    const totalExpenses = fsiTotalCosts + (daChargesCosts.totalDACharges || 0) + (societyCosts.totalSocietyExpenses || 0) + 
                     totalConstructionCost + totalMarketingCost + totalMiscExp + totalBrokerageCommission + totalBankInterest;
    
    // Cost per sft = Total Expenses / Saleable Area (in sqft)
    const costPerSqft = finalSaleAreaSqft > 0 ? totalExpenses / finalSaleAreaSqft : 0;

    return {
      fsiCosts: fsiTotalCosts,
      daCharges: daChargesCosts.totalDACharges || 0,
      societyExpenses: societyCosts.totalSocietyExpenses || 0,
      constructionCost: totalConstructionCost,
      marketingCost: totalMarketingCost,
      miscExpenses: totalMiscExp,
      brokerageCommission: totalBrokerageCommission,
      bankInterest: totalBankInterest,
      totalExpenses,
      totalRevenue,
      saleableAreaSqft: finalSaleAreaSqft,
      costPerSqft
    };
  }, [fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, customConstructionCost, customSaleRate,
      customFreeArea, customSocietyShare, areaInputMode, societyShareToFreeArea,
      formData.carpetAreaFactor, formData.loadingPercentage,
      formData.areaFlats, formData.areaOffices, formData.areaShops,
      formData.marketingCostPerSqft, formData.miscExpPerSqft,
      formData.brokerageCommissionType, formData.brokerageCommissionFixed, formData.brokerageCommissionPerSqft,
      formData.projectDuration, formData.projectInterestRate]);

  // Get max and min profits for bar chart scaling
  const maxSaleRateProfit = Math.max(...saleRateSensitivity.map(s => s.profit));
  const minSaleRateProfit = Math.min(...saleRateSensitivity.map(s => s.profit));
  const saleRateRange = maxSaleRateProfit - minSaleRateProfit;

  const maxFreeAreaProfit = Math.max(...freeAreaSensitivity.map(s => s.profit));
  const minFreeAreaProfit = Math.min(...freeAreaSensitivity.map(s => s.profit));
  const freeAreaRange = maxFreeAreaProfit - minFreeAreaProfit;

  // Construction cost chart scaling
  const maxConstructionCostProfit = Math.max(...constructionCostSensitivity.map(s => s.profit));
  const minConstructionCostProfit = Math.min(...constructionCostSensitivity.map(s => s.profit));
  const constructionCostRange = maxConstructionCostProfit - minConstructionCostProfit;

  if (!fsiBreakdown) {
    return (
      <Card className="rounded-sm shadow-sm bg-secondary/50">
        <CardContent className="py-12 text-center text-muted-foreground">
          <p className="text-lg font-medium">Complete Previous Tabs First</p>
          <p className="text-sm mt-2">Please fill in all required information to generate sensitivity analysis</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Base Values Card */}
      <Card className="rounded-sm shadow-sm bg-secondary/30">
        <CardContent className="py-4">
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Base Sale Rate</p>
              <p className="font-mono font-bold text-lg">₹{baseSaleRate.toLocaleString()}/sft</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Base Free Area</p>
              <p className="font-mono font-bold text-lg">{baseFreeAreaPercent}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Base Profit</p>
              <p className={`font-mono font-bold text-lg ${baseProfit >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                {formatCrores(baseProfit)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Area Sharing</p>
              <p className="font-mono font-bold text-lg">
                <span className="text-blue-600">{baseAreaSharing.societyShare}%</span>
                <span className="text-muted-foreground mx-1">:</span>
                <span className="text-chart-1">{baseAreaSharing.builderShare}%</span>
              </p>
              <p className="text-xs text-muted-foreground">{formData.projectMode === "greenfield" ? "Landowner : Developer" : "Society : Builder"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Area Sharing Breakdown */}
      <Card className="rounded-sm shadow-sm border-blue-500/30" data-testid="area-sharing-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <PieChart className="h-5 w-5 text-blue-500" />
            <CardTitle className="font-heading tracking-tight">Area Sharing Ratio</CardTitle>
          </div>
          <CardDescription>Distribution of total carpet area between society and builder</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Visual Bar */}
            <div className="space-y-3">
              <div className="h-8 rounded-sm overflow-hidden flex">
                <div 
                  className="bg-blue-500 flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${baseAreaSharing.societyShare}%` }}
                >
                  {baseAreaSharing.societyShare > 15 && `${baseAreaSharing.societyShare}%`}
                </div>
                <div 
                  className="bg-chart-1 flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${baseAreaSharing.builderShare}%` }}
                >
                  {baseAreaSharing.builderShare > 15 && `${baseAreaSharing.builderShare}%`}
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-blue-500" />
                  <span>Society ({baseAreaSharing.societyShare}%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-chart-1" />
                  <span>Builder ({baseAreaSharing.builderShare}%)</span>
                </div>
              </div>
            </div>
            {/* Area Details */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-xs text-muted-foreground">Total Carpet</p>
                <p className="font-mono font-semibold">{Math.round(baseAreaSharing.totalCarpetArea).toLocaleString()} sq m</p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-sm border border-blue-500/30">
                <p className="text-xs text-blue-600">Society Area</p>
                <p className="font-mono font-semibold text-blue-600">{Math.round(baseAreaSharing.societyArea).toLocaleString()} sq m</p>
              </div>
              <div className="p-3 bg-chart-1/10 rounded-sm border border-chart-1/30">
                <p className="text-xs text-chart-1">Builder Area</p>
                <p className="font-mono font-semibold text-chart-1">{Math.round(baseAreaSharing.builderArea).toLocaleString()} sq m</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart 1: Sale Rate Sensitivity */}
      <Card className="rounded-sm shadow-sm" data-testid="sale-rate-sensitivity-chart">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Sale Rate Sensitivity Analysis</CardTitle>
          <CardDescription>Impact of ₹500/sft changes on project profitability</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Change</th>
                  <th className="text-left py-2 px-3">Sale Rate</th>
                  <th className="text-left py-2 px-3">Profit</th>
                  <th className="text-left py-2 px-3 w-1/2">Visualization</th>
                </tr>
              </thead>
              <tbody>
                {saleRateSensitivity.map((scenario) => {
                  const profitPercent = saleRateRange > 0 
                    ? ((scenario.profit - minSaleRateProfit) / saleRateRange) * 100 
                    : 50;
                  const isProfitable = scenario.profit >= 0;
                  
                  return (
                    <tr 
                      key={`salerate-${scenario.rate}-${scenario.change}`} 
                      className={`border-b ${scenario.isBase ? 'bg-accent/20 font-semibold' : ''}`}
                    >
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {scenario.change > 0 ? (
                            <TrendingUp className="h-4 w-4 text-chart-1" />
                          ) : scenario.change < 0 ? (
                            <TrendingDown className="h-4 w-4 text-destructive" />
                          ) : (
                            <Minus className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={scenario.change > 0 ? 'text-chart-1' : scenario.change < 0 ? 'text-destructive' : ''}>
                            {scenario.change > 0 ? `+₹${scenario.change}` : scenario.change === 0 ? 'Base' : `₹${scenario.change}`}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-3 font-mono">₹{scenario.saleRate.toLocaleString()}</td>
                      <td className={`py-3 px-3 font-mono ${isProfitable ? 'text-chart-1' : 'text-destructive'}`}>
                        {formatCrores(scenario.profit)}
                      </td>
                      <td className="py-3 px-3">
                        <div className="h-6 bg-secondary/50 rounded-sm overflow-hidden">
                          <div 
                            className={`h-full transition-all ${isProfitable ? 'bg-chart-1' : 'bg-destructive'} ${scenario.isBase ? 'opacity-100' : 'opacity-70'}`}
                            style={{ width: `${Math.max(5, profitPercent)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Chart 2: Free Area Sensitivity */}
      <Card className="rounded-sm shadow-sm" data-testid="free-area-sensitivity-chart">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Free Area Sensitivity Analysis</CardTitle>
          <CardDescription>Impact of additional free area (over {baseFreeAreaPercent}% base) on project profitability</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Additional %</th>
                  <th className="text-left py-2 px-3">Total Free Area %</th>
                  <th className="text-left py-2 px-3">Profit</th>
                  <th className="text-left py-2 px-3 w-1/2">Visualization</th>
                </tr>
              </thead>
              <tbody>
                {/* Base row */}
                <tr className="border-b bg-accent/20 font-semibold">
                  <td className="py-3 px-3">Base (0%)</td>
                  <td className="py-3 px-3 font-mono">{baseFreeAreaPercent}%</td>
                  <td className={`py-3 px-3 font-mono ${baseProfit >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                    {formatCrores(baseProfit)}
                  </td>
                  <td className="py-3 px-3">
                    <div className="h-6 bg-secondary/50 rounded-sm overflow-hidden">
                      <div 
                        className={`h-full ${baseProfit >= 0 ? 'bg-chart-1' : 'bg-destructive'}`}
                        style={{ width: `${freeAreaRange > 0 ? Math.max(5, ((baseProfit - minFreeAreaProfit) / freeAreaRange) * 100) : 50}%` }}
                      />
                    </div>
                  </td>
                </tr>
                {freeAreaSensitivity.map((scenario) => {
                  const profitPercent = freeAreaRange > 0 
                    ? ((scenario.profit - minFreeAreaProfit) / freeAreaRange) * 100 
                    : 50;
                  const isProfitable = scenario.profit >= 0;
                  const profitChange = scenario.profit - baseProfit;
                  
                  return (
                    <tr key={`freearea-${scenario.additionalPercent}-${scenario.totalFreePercent}`} className="border-b">
                      <td className="py-3 px-3 text-destructive">+{scenario.additionalPercent}%</td>
                      <td className="py-3 px-3 font-mono">{scenario.totalFreePercent}%</td>
                      <td className="py-3 px-3">
                        <div className={`font-mono ${isProfitable ? 'text-chart-1' : 'text-destructive'}`}>
                          {formatCrores(scenario.profit)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ({profitChange >= 0 ? '+' : ''}{formatCrores(profitChange)})
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="h-6 bg-secondary/50 rounded-sm overflow-hidden">
                          <div 
                            className={`h-full transition-all opacity-70 ${isProfitable ? 'bg-chart-1' : 'bg-destructive'}`}
                            style={{ width: `${Math.max(5, profitPercent)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Chart 3: Construction Cost Sensitivity */}
      <Card className="rounded-sm shadow-sm" data-testid="construction-cost-sensitivity-chart">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Construction Cost Sensitivity Analysis</CardTitle>
          <CardDescription>Impact of ₹100/sft changes in construction cost on project profitability</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Change</th>
                  <th className="text-left py-2 px-3">Cost/sft</th>
                  <th className="text-left py-2 px-3">Profit</th>
                  <th className="text-left py-2 px-3 w-1/2">Visualization</th>
                </tr>
              </thead>
              <tbody>
                {constructionCostSensitivity.map((scenario) => {
                  const profitPercent = constructionCostRange > 0 
                    ? ((scenario.profit - minConstructionCostProfit) / constructionCostRange) * 100 
                    : 50;
                  const isProfitable = scenario.profit >= 0;
                  const profitChange = scenario.profit - baseProfit;
                  
                  return (
                    <tr 
                      key={`constcost-${scenario.cost}-${scenario.change}`} 
                      className={`border-b ${scenario.isBase ? 'bg-accent/20 font-semibold' : ''}`}
                    >
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {scenario.change < 0 ? (
                            <TrendingUp className="h-4 w-4 text-chart-1" />
                          ) : scenario.change > 0 ? (
                            <TrendingDown className="h-4 w-4 text-destructive" />
                          ) : (
                            <Minus className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={scenario.change < 0 ? 'text-chart-1' : scenario.change > 0 ? 'text-destructive' : ''}>
                            {scenario.change < 0 ? `₹${scenario.change}` : scenario.change === 0 ? 'Base' : `+₹${scenario.change}`}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-3 font-mono">₹{scenario.constructionCost.toLocaleString()}</td>
                      <td className="py-3 px-3">
                        <div className={`font-mono ${isProfitable ? 'text-chart-1' : 'text-destructive'}`}>
                          {formatCrores(scenario.profit)}
                        </div>
                        {!scenario.isBase && (
                          <div className="text-xs text-muted-foreground">
                            ({profitChange >= 0 ? '+' : ''}{formatCrores(profitChange)})
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="h-6 bg-secondary/50 rounded-sm overflow-hidden">
                          <div 
                            className={`h-full transition-all ${isProfitable ? 'bg-chart-1' : 'bg-destructive'} ${scenario.isBase ? 'opacity-100' : 'opacity-70'}`}
                            style={{ width: `${Math.max(5, profitPercent)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Chart 4: Basic FSI (1.1) Cost Sensitivity — informational scenario */}
      {basicFSISensitivity && (
        <Card className="rounded-sm shadow-sm border-blue-200" data-testid="basic-fsi-sensitivity">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-blue-600" />
              <CardTitle className="font-heading tracking-tight">Basic FSI (1.1 + Full Ancillary) Cost Sensitivity</CardTitle>
            </div>
            <CardDescription>
              Profit impact if the project drops Paid FSI + TDR premium purchases. Basic FSI (1.1) plus the full Ancillary FSI (as sanctioned in Normal FSI) is included. Purely informational — feasibility calculations remain at full FSI cost.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Initial Investment comparison — two big numbers side by side */}
            {(() => {
              const basicRow = basicFSISensitivity.find(s => s.blendPct === 0);
              const fullRow = basicFSISensitivity.find(s => s.isBase);
              if (!basicRow || !fullRow) return null;
              const savings = Math.max(0, (fullRow.initialInvestment || 0) - (basicRow.initialInvestment || 0));
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 border border-blue-200 bg-blue-50/40 rounded-sm" data-testid="initial-investment-comparison">
                  <div className="p-3 bg-white rounded-sm border">
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Initial Investment — Full Sanction</div>
                    <div className="font-mono font-bold text-xl mt-1" data-testid="ii-full-sanction">{formatCrores(fullRow.initialInvestment)}</div>
                    <div className="text-[10px] text-accent font-semibold">Current feasibility</div>
                  </div>
                  <div className="p-3 bg-white rounded-sm border border-blue-300">
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Initial Investment — Basic Sanction</div>
                    <div className="font-mono font-bold text-xl text-blue-700 mt-1" data-testid="ii-basic-sanction">{formatCrores(basicRow.initialInvestment)}</div>
                    <div className="text-[10px] text-blue-700 font-semibold">Basic FSI 1.1 + full ancillary</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-sm border border-green-200 flex flex-col justify-center">
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Upfront Capital Saved</div>
                    <div className="font-mono font-bold text-xl text-green-700 mt-1" data-testid="ii-savings">{formatCrores(savings)}</div>
                    <div className="text-[10px] text-green-700 font-semibold">If Basic Sanction is chosen</div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b-2 border-blue-200">
                    <th className="text-left p-2.5 font-semibold">Scenario</th>
                    <th className="text-right p-2.5 font-semibold">FSI Costs</th>
                    <th className="text-right p-2.5 font-semibold">Cost Savings</th>
                    <th className="text-right p-2.5 font-semibold">Initial Investment</th>
                    <th className="text-right p-2.5 font-semibold">Net Profit</th>
                    <th className="text-right p-2.5 font-semibold">Δ Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {basicFSISensitivity.map((sc) => {
                    const baseProf = basicFSISensitivity.find(s => s.isBase)?.profit ?? 0;
                    const dProfit = sc.profit - baseProf;
                    return (
                      <tr key={sc.blendPct} className={`border-b border-blue-100 ${sc.isBase ? 'bg-accent/10 font-semibold' : 'hover:bg-blue-50/40'}`}>
                        <td className="p-2.5">
                          {sc.label}
                          {sc.isBase && <span className="ml-2 text-[10px] text-accent font-semibold">← Current</span>}
                        </td>
                        <td className="p-2.5 text-right font-mono">{formatCrores(sc.fsiCost)}</td>
                        <td className="p-2.5 text-right font-mono text-green-700">{sc.savingsVsFull > 0 ? formatCrores(sc.savingsVsFull) : "—"}</td>
                        <td className="p-2.5 text-right font-mono">{formatCrores(sc.initialInvestment)}</td>
                        <td className="p-2.5 text-right font-mono">{formatCrores(sc.profit)}</td>
                        <td className={`p-2.5 text-right font-mono ${dProfit > 0 ? 'text-green-700' : dProfit < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {dProfit === 0 ? "—" : `${dProfit > 0 ? '+' : ''}${formatCrores(dProfit)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground italic">
              Note: This is a strategy comparison only. The active feasibility always uses Full FSI cost — see the &ldquo;Basic FSI 1.1 only&rdquo; info card on the FSI Costs tab.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Custom Profit Calculator */}
      <Card className="rounded-sm shadow-sm border-chart-1/50" data-testid="custom-profit-calculator">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-chart-1" />
            <CardTitle className="font-heading tracking-tight">Custom Profit Calculator</CardTitle>
          </div>
          <CardDescription>Calculate profit with custom combinations of construction cost, area sharing, and sale rate</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Construction Cost Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="custom-construction-cost">Construction Cost (₹/sft)</Label>
              <Select
                value={customConstructionCost}
                onValueChange={(value) => setCustomConstructionCost(value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="custom-construction-cost-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 21}, (_, i) => 2000 + (i * 100)).map(cost => (
                    <SelectItem key={cost} value={cost.toString()}>₹{cost.toLocaleString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Area Input Toggle + Dropdown */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Area Input Method</Label>
                <RadioGroup 
                  value={areaInputMode} 
                  onValueChange={setAreaInputMode}
                  className="flex gap-2"
                >
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="freeArea" id="mode-free" className="h-3 w-3" />
                    <Label htmlFor="mode-free" className="text-xs cursor-pointer">Free %</Label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="sharingRatio" id="mode-share" className="h-3 w-3" />
                    <Label htmlFor="mode-share" className="text-xs cursor-pointer">Society %</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {areaInputMode === "freeArea" ? (
                <Select
                  value={customFreeArea}
                  onValueChange={(value) => setCustomFreeArea(value)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="custom-free-area-select">
                    <SelectValue placeholder="Free Area %" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 21}, (_, i) => i).map(percent => (
                      <SelectItem key={percent} value={percent.toString()}>{percent}% Free Area</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={customSocietyShare}
                  onValueChange={(value) => setCustomSocietyShare(value)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="custom-society-share-select">
                    <SelectValue placeholder={formData.projectMode === "greenfield" ? "Landowner Share %" : "Society Share %"} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 21}, (_, i) => 20 + (i * 2)).map(percent => (
                      <SelectItem key={percent} value={percent.toString()}>{percent}% {formData.projectMode === "greenfield" ? "Landowner" : "Society"} : {100-percent}% {formData.projectMode === "greenfield" ? "Developer" : "Builder"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                {areaInputMode === "freeArea" 
                  ? "Additional free area given to society" 
                  : formData.projectMode === "greenfield" ? "Landowner's share of total carpet area" : "Society's share of total carpet area"}
              </p>
            </div>

            {/* Sale Rate Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="custom-sale-rate">Sale Rate (₹/sft)</Label>
              <Select
                value={customSaleRate}
                onValueChange={(value) => setCustomSaleRate(value)}
              >
                <SelectTrigger className="rounded-sm" data-testid="custom-sale-rate-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 61}, (_, i) => 5000 + (i * 500)).map(rate => (
                    <SelectItem key={rate} value={rate.toString()}>₹{rate.toLocaleString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* RR Cost Multiplier */}
            <div className="space-y-2">
              <Label htmlFor="rr-multiplier">RR Cost Increase (%)</Label>
              <Select
                value={rrMultiplier}
                onValueChange={handleRRMultiplierChange}
              >
                <SelectTrigger className="rounded-sm" data-testid="rr-multiplier-select">
                  <SelectValue placeholder="Select %" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 21}, (_, i) => i).map(percent => (
                    <SelectItem key={percent} value={percent.toString()}>{percent}% RR Increase</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Increases all Ready Reckoner based costs</p>
            </div>
          </div>

          <Separator />

          {/* Result Display with Area Sharing */}
          <div className={`p-4 rounded-sm border-2 ${customProfit >= 0 ? 'border-chart-1/50 bg-chart-1/10' : 'border-destructive/50 bg-destructive/10'}`}>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Calculated Profit</p>
                <p className={`font-mono font-bold text-2xl ${customProfit >= 0 ? 'text-chart-1' : 'text-destructive'}`} data-testid="custom-profit-result">
                  {formatCrores(customProfit)}
                </p>
                <p className={`text-xs ${customProfit - baseProfit >= 0 ? 'text-chart-1' : 'text-destructive'}`}>
                  vs Base: {customProfit - baseProfit >= 0 ? '+' : ''}{formatCrores(customProfit - baseProfit)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Area Sharing</p>
                <p className="font-mono font-bold text-lg">
                  <span className="text-blue-600">{customAreaSharing.societyShare}%</span>
                  <span className="mx-1">:</span>
                  <span className="text-chart-1">{customAreaSharing.builderShare}%</span>
                </p>
                <p className="text-xs text-muted-foreground">{formData.projectMode === "greenfield" ? "Landowner : Developer" : "Society : Builder"}</p>
              </div>
              <div className="text-sm space-y-1 text-muted-foreground">
                <p>Construction: ₹{parseInt(customConstructionCost).toLocaleString()}/sft</p>
                <p>Sale Rate: ₹{parseInt(customSaleRate).toLocaleString()}/sft</p>
              </div>
              <div className="text-sm space-y-1">
                <p className="text-muted-foreground">Society: <span className="text-blue-600 font-mono">{Math.round(customAreaSharing.societyArea).toLocaleString()} sqm</span></p>
                <p className="text-muted-foreground">Builder: <span className="text-chart-1 font-mono">{Math.round(customAreaSharing.builderArea).toLocaleString()} sqm</span></p>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          {costBreakdown && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Cost & Revenue Breakdown</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Expenses Column */}
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <h5 className="font-semibold text-destructive">Total Expenses</h5>
                    <div className="flex justify-between"><span className="text-muted-foreground">FSI & Sanction Costs</span><span className="font-mono">{formatCurrency(costBreakdown.fsiCosts)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">DA Charges</span><span className="font-mono">{formatCurrency(costBreakdown.daCharges)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Society Expenses</span><span className="font-mono">{formatCurrency(costBreakdown.societyExpenses)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Construction Cost</span><span className="font-mono">{formatCurrency(costBreakdown.constructionCost)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Marketing Cost</span><span className="font-mono">{formatCurrency(costBreakdown.marketingCost)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Misc Expenses</span><span className="font-mono">{formatCurrency(costBreakdown.miscExpenses)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Brokerage Commission</span><span className="font-mono">{formatCurrency(costBreakdown.brokerageCommission)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Bank Interest</span><span className="font-mono">{formatCurrency(costBreakdown.bankInterest)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-semibold"><span>Total Expenses</span><span className="font-mono text-destructive">{formatCurrency(costBreakdown.totalExpenses)}</span></div>
                    <Separator />
                    <div className="flex justify-between"><span className="text-muted-foreground">Saleable Area</span><span className="font-mono">{Math.round(costBreakdown.saleableAreaSqft).toLocaleString()} sft</span></div>
                    <div className="flex justify-between font-semibold bg-destructive/10 p-2 rounded -mx-1">
                      <span className="text-destructive">Cost per Sft</span>
                      <span className="font-mono text-destructive">₹{Math.round(costBreakdown.costPerSqft).toLocaleString()}/sft</span>
                    </div>
                  </div>
                  
                  {/* Revenue Column */}
                  <div className="space-y-2 text-sm bg-chart-1/10 p-3 rounded-sm">
                    <h5 className="font-semibold text-chart-1">Revenue & Profit</h5>
                    <div className="flex justify-between"><span className="text-muted-foreground">Total Revenue</span><span className="font-mono">{formatCurrency(costBreakdown.totalRevenue)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Total Expenses</span><span className="font-mono">({formatCurrency(costBreakdown.totalExpenses)})</span></div>
                    <Separator />
                    <div className="flex justify-between font-semibold"><span>Net Profit</span><span className={`font-mono ${(costBreakdown.totalRevenue - costBreakdown.totalExpenses) >= 0 ? 'text-chart-1' : 'text-destructive'}`}>{formatCurrency(costBreakdown.totalRevenue - costBreakdown.totalExpenses)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Profit Margin</span><span className="font-mono">{costBreakdown.totalRevenue > 0 ? (((costBreakdown.totalRevenue - costBreakdown.totalExpenses) / costBreakdown.totalRevenue) * 100).toFixed(1) : 0}%</span></div>
                    <Separator />
                    <div className="flex justify-between"><span className="text-muted-foreground">Sale Rate</span><span className="font-mono">₹{parseInt(customSaleRate).toLocaleString()}/sft</span></div>
                    <div className="flex justify-between font-semibold bg-chart-1/20 p-2 rounded -mx-1">
                      <span className="text-chart-1">Profit per Sft</span>
                      <span className="font-mono text-chart-1">₹{Math.round(parseInt(customSaleRate) - costBreakdown.costPerSqft).toLocaleString()}/sft</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Investment at Start of Project */}
          {investmentSummary && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Total Investment at Start of Project</h4>
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-sm">
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between font-semibold">
                        <span className="text-muted-foreground">TDR Cost</span>
                        <span className="font-mono">{formatCurrency(investmentSummary.tdrCost)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid FSI + Ancillary (Full)</span>
                        <span className="font-mono">{formatCurrency(investmentSummary.paidFSIAncillaryFull)}</span>
                      </div>
                      <div className="flex justify-between text-amber-600">
                        <span>@ 20% (min ₹50L)</span>
                        <span className="font-mono font-semibold">{formatCurrency(investmentSummary.paidFSIAncillaryInvestment)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between"><span className="text-muted-foreground">Other Sanction Costs</span><span className="font-mono">{formatCurrency(investmentSummary.sanctionCosts)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">DA Charges</span><span className="font-mono">{formatCurrency(investmentSummary.daCharges)}</span></div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between"><span className="text-muted-foreground">Shifting (50%)</span><span className="font-mono">{formatCurrency(investmentSummary.shiftingCharges)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Betterment (if split)</span><span className="font-mono">{formatCurrency(investmentSummary.bettermentCharges)}</span></div>
                      {investmentSummary.bgBaseAmount > 0 && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Bank Guarantee</span><span className="font-mono">{formatCurrency(investmentSummary.bgBaseAmount)}</span></div>
                      )}
                      {investmentSummary.depositCost > 0 && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Deposit</span><span className="font-mono">{formatCurrency(investmentSummary.depositCost)}</span></div>
                      )}
                      {investmentSummary.brokerageCostSociety > 0 && (
                        <div className="flex justify-between"><span className="text-muted-foreground">Brokerage (Society)</span><span className="font-mono">{formatCurrency(investmentSummary.brokerageCostSociety)}</span></div>
                      )}
                      {investmentSummary.ulcCharges > 0 && (
                        <div className="flex justify-between"><span className="text-muted-foreground">ULC Sec 20 Charges</span><span className="font-mono">{formatCurrency(investmentSummary.ulcCharges)}</span></div>
                      )}
                    </div>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex justify-between font-bold text-lg">
                    <span className="text-amber-600">Total Upfront Investment</span>
                    <span className="font-mono text-amber-600">{formatCrores(investmentSummary.totalInvestment)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">TDR at full cost + Paid FSI/Ancillary @ 20% (min ₹50 Lakhs)</p>
                </div>

                {/* Alternate: Basic FSI 1.1 sanction only (TDR + Paid FSI deferred) */}
                {investmentSummary.basicMode && (
                  <div
                    className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-sm mt-4"
                    data-testid="basic-investment-summary"
                  >
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-emerald-700 mb-3">
                      Alternate: Basic FSI 1.1 Sanction Only (at start of project)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex justify-between text-muted-foreground italic">
                          <span>TDR Cost</span>
                          <span className="font-mono">Deferred (₹0)</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground italic">
                          <span>Paid FSI</span>
                          <span className="font-mono">Deferred (₹0)</span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-semibold">
                          <span className="text-muted-foreground">Basic Sanction (incl. full Ancillary)</span>
                          <span className="font-mono" data-testid="basic-sanction-cost">
                            {formatCurrency(investmentSummary.basicMode.basicSanctionCosts)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">DA Charges</span>
                          <span className="font-mono">{formatCurrency(investmentSummary.basicMode.daCharges)}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Shifting (50%)</span>
                          <span className="font-mono">{formatCurrency(investmentSummary.basicMode.shiftingCharges)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Betterment (if split)</span>
                          <span className="font-mono">{formatCurrency(investmentSummary.basicMode.bettermentCharges)}</span>
                        </div>
                        {investmentSummary.basicMode.bgBaseAmount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bank Guarantee</span>
                            <span className="font-mono">{formatCurrency(investmentSummary.basicMode.bgBaseAmount)}</span>
                          </div>
                        )}
                        {investmentSummary.basicMode.depositCost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Deposit</span>
                            <span className="font-mono">{formatCurrency(investmentSummary.basicMode.depositCost)}</span>
                          </div>
                        )}
                        {investmentSummary.basicMode.brokerageCostSociety > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Brokerage (Society)</span>
                            <span className="font-mono">{formatCurrency(investmentSummary.basicMode.brokerageCostSociety)}</span>
                          </div>
                        )}
                        {investmentSummary.basicMode.ulcCharges > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">ULC Sec 20 Charges</span>
                            <span className="font-mono">{formatCurrency(investmentSummary.basicMode.ulcCharges)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex justify-between font-bold text-lg">
                      <span className="text-emerald-700">Total Upfront (Basic FSI Only)</span>
                      <span className="font-mono text-emerald-700" data-testid="basic-total-upfront">
                        {formatCrores(investmentSummary.basicMode.basicTotalInvestment)}
                      </span>
                    </div>
                    {investmentSummary.basicMode.savingsVsFull > 0 && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-emerald-700">Upfront capital saved vs Full Sanction</span>
                        <span className="font-mono font-semibold text-emerald-700" data-testid="basic-savings">
                          {formatCrores(investmentSummary.basicMode.savingsVsFull)}
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Assumes developer sanctions only Basic FSI (1.1) + full Ancillary at project start; TDR &amp; Paid FSI purchases deferred to later phases.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="rounded-sm shadow-sm border-accent/50">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight text-accent">Key Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Sale Rate Impact</h4>
              <p className="text-sm text-muted-foreground">
                Every ₹500/sft increase in sale rate adds approximately{' '}
                <span className="font-mono text-chart-1">
                  {(() => {
                    const scenario = saleRateSensitivity.find(s => s.change === 500);
                    const impact = scenario ? scenario.profit - baseProfit : 0;
                    // Fallback: direct calculation if sensitivity returns 0 but there's saleable area
                    if (Math.abs(impact) < 1 && baseMetrics?.finalSaleAreaSqft > 0) {
                      return formatCrores(500 * baseMetrics.finalSaleAreaSqft);
                    }
                    return formatCrores(impact);
                  })()}
                </span>
                {' '}to profit.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Free Area Impact</h4>
              <p className="text-sm text-muted-foreground">
                Each 1% increase in free area reduces profit by approximately{' '}
                <span className="font-mono text-destructive">
                  {formatCrores(baseProfit - (freeAreaSensitivity[0]?.profit || 0))}
                </span>
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Construction Cost Impact</h4>
              <p className="text-sm text-muted-foreground">
                Each ₹100/sft increase in construction cost reduces profit by approximately{' '}
                <span className="font-mono text-destructive">
                  {formatCrores(baseProfit - (constructionCostSensitivity.find(s => s.change === 100)?.profit || 0))}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SensitivityAnalysis;
